/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Camera-ready fly-through for recordings and social clips.
 * `/about?demo=journey` hides the site chrome so the Three.js canvas
 * fills the viewport. The page also exposes `window.__JOURNEY_DEMO__`
 * for `scripts/record-about-journey.mjs`.
 */

import { useRouterState } from '@tanstack/react-router';
import { readSiteHeaderHeight } from '@/lib/siteHeader';

export const JOURNEY_DEMO_PARAM = 'demo';
export const JOURNEY_DEMO_VALUE = 'journey';

export type AboutDemoSearch = {
  demo?: typeof JOURNEY_DEMO_VALUE;
};

export type JourneyDemoApi = {
  ready: boolean;
  scrollToProgress: (progress: number) => void;
  record: (opts?: { durationMs?: number; fps?: number }) => Promise<{
    bytes: number;
    mimeType: string;
    blob: Blob;
  }>;
};

declare global {
  interface Window {
    __JOURNEY_DEMO__?: JourneyDemoApi;
  }
}

export function parseJourneyDemoSearch(search: Record<string, unknown>): AboutDemoSearch {
  return {
    demo: search[JOURNEY_DEMO_PARAM] === JOURNEY_DEMO_VALUE ? JOURNEY_DEMO_VALUE : undefined,
  };
}

export function isJourneyDemoSearch(search: unknown): boolean {
  if (typeof search === 'string') {
    const query = search.startsWith('?') ? search.slice(1) : search;
    return new URLSearchParams(query).get(JOURNEY_DEMO_PARAM) === JOURNEY_DEMO_VALUE;
  }
  if (search && typeof search === 'object' && JOURNEY_DEMO_PARAM in search) {
    return (search as { demo?: unknown }).demo === JOURNEY_DEMO_VALUE;
  }
  return false;
}

export function useJourneyDemo(): boolean {
  return useRouterState({
    select: (state) => {
      const location = state.location as { searchStr?: string; search: unknown };
      return isJourneyDemoSearch(location.searchStr ?? location.search);
    },
  });
}

export function journeyCanvas(): HTMLCanvasElement | null {
  return document.querySelector<HTMLCanvasElement>('#about-journey canvas');
}

export function scrollJourneyToProgress(progress: number, track: HTMLElement | null = null) {
  const el = track ?? document.getElementById('about-journey');
  if (!el) return;
  const header = readSiteHeaderHeight();
  const view = window.innerHeight - header;
  const max = Math.max(0, el.offsetHeight - view);
  const trackTop = window.scrollY + el.getBoundingClientRect().top - header;
  window.scrollTo(0, trackTop + Math.min(1, Math.max(0, progress)) * max);
}

function pickRecorderMime(): string {
  const candidates = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? '';
}

export async function recordJourneyCanvas(opts: {
  durationMs?: number;
  fps?: number;
  track?: HTMLElement | null;
} = {}): Promise<{ bytes: number; mimeType: string; blob: Blob }> {
  const canvas = journeyCanvas();
  if (!canvas) {
    throw new Error('journey canvas is not ready');
  }
  const mimeType = pickRecorderMime();
  if (!mimeType || typeof MediaRecorder === 'undefined') {
    throw new Error('MediaRecorder WebM is not supported in this browser');
  }

  const durationMs = opts.durationMs ?? 56_000;
  const fps = opts.fps ?? 30;
  scrollJourneyToProgress(0, opts.track);
  await new Promise((resolve) => window.setTimeout(resolve, 300));
  const stream = canvas.captureStream(fps);
  const chunks: BlobPart[] = [];
  const recorder = new MediaRecorder(stream, {
    mimeType,
    videoBitsPerSecond: 8_000_000,
  });
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };

  const started = performance.now();
  const blob = await new Promise<Blob>((resolve, reject) => {
    recorder.onerror = () => {
      reject(recorder.error ?? new Error('MediaRecorder failed'));
    };
    recorder.onstop = () => {
      resolve(new Blob(chunks, { type: mimeType }));
    };
    recorder.start(200);
    const tick = () => {
      const elapsed = performance.now() - started;
      const progress = Math.min(1, elapsed / durationMs);
      scrollJourneyToProgress(progress, opts.track);
      if (progress < 1) {
        requestAnimationFrame(tick);
        return;
      }
      scrollJourneyToProgress(1, opts.track);
      window.setTimeout(() => {
        if (recorder.state !== 'inactive') recorder.stop();
        for (const track of stream.getTracks()) track.stop();
      }, 400);
    };
    requestAnimationFrame(tick);
  });

  return { bytes: blob.size, mimeType, blob };
}

export function bindJourneyDemoApi(track: HTMLElement | null): () => void {
  const api: JourneyDemoApi = {
    get ready() {
      const canvas = journeyCanvas();
      return Boolean(canvas && canvas.width > 0 && canvas.height > 0);
    },
    scrollToProgress(progress) {
      scrollJourneyToProgress(progress, track);
    },
    record(opts) {
      return recordJourneyCanvas({ ...opts, track });
    },
  };
  window.__JOURNEY_DEMO__ = api;
  return () => {
    if (window.__JOURNEY_DEMO__ === api) {
      delete window.__JOURNEY_DEMO__;
    }
  };
}
